'use client';
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '@/redux/Hook';
import {
  getActivationStatus,
  getAllSubscriptionPlanList,
  verifyCouponCode,
} from '@/redux/subscriptionPlan/ThunkAction';

import { iErrorResponse } from '@/customTypes/CommonServiceTypes';
import toast from 'react-hot-toast';
import { connectRequestWithWholesaler, getWholesalerList } from '@/redux/wholesaler/ThunkAction';
import { RootState } from '@/redux';
import { getUserCountry, getUserExchangeRateWithCountry } from '@/utils/getUserLocation';
import { currencyMap } from '@/utils/currencyMap';
import { addMoneyToWallet } from '@/redux/wallet/ThunkActions';
import { getMissingPayUFields, normalizePayUPayload, submitPayUHtmlForm } from '@/utils/payu';
import {
  PiArrowRightBold,
  PiCheckCircleFill,
  PiCrownSimpleBold,
  PiLightningFill,
  PiXBold,
  PiTagBold,
  PiWarningBold,
  PiSealCheckFill,
  PiWalletBold,
} from 'react-icons/pi';
import { subscriptiontUpgrade } from '@/redux/subscriptiontUpgrade/ThunkAction';
import { useRouter } from 'next/navigation';
import { routes } from '@/config/routes';

interface SubscriptionPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  showCloseButton?: boolean;
  isUpgradeFlow?: boolean;
}

const ease = [0.22, 0.68, 0, 1.1];

const SubscriptionPurchaseModal: React.FC<SubscriptionPurchaseModalProps> = ({
  isOpen,
  onClose,
  showCloseButton,
  isUpgradeFlow = false,
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const [errorMessage, setErrorMessage] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [activeCouponPlanId, setActiveCouponPlanId] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [validCoupon, setValidCoupon] = useState(false);
  const [couponFixedPrice, setCouponFixedPrice] = useState<number | null>(null);
  const [userCurrencyCode, setUserCurrencyCode] = useState('INR');
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [authToken, setAuthToken] = useState('');
  const [remainingBalance, setRemainingBalance] = useState<number | null>(null);
  const [planType, setPlanType] = useState<'Monthly' | 'Yearly'>('Monthly');
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [modalState, setModalState] = useState<{
    type: 'error' | 'confirm' | 'success' | null;
    message?: string;
    plan?: any;
  }>({ type: null });
  const [currencySymbol, setCurrencySymbol] = useState('₹');

  const { planList, isVerifyCouponCode } = useAppSelector((state) => state.subscriptionPlan);
  const { activationStatus } = useAppSelector((state) => state.subscriptionPlan);
  const { user } = useAppSelector((state: RootState) => state.user);
  const { allWholesalerList } = useAppSelector((state: RootState) => state.wholersaler);

  useEffect(() => {
    const tokenData = localStorage.getItem('userToken');
    if (tokenData) setAuthToken(tokenData);
    dispatch(getWholesalerList({ search: 'all' }));
    dispatch(getActivationStatus());
    dispatch(getAllSubscriptionPlanList());
  }, [dispatch]);

  const hasYearlyPlans = planList?.some((p) => (p.durationUnit || '').toLowerCase().startsWith('year')) ?? false;

  const filteredPlans = planList
    ?.filter((plan) => {
      const unit = (plan.durationUnit || '').toLowerCase();
      return planType === 'Monthly' ? unit.startsWith('month') : unit.startsWith('year');
    })
    ?.sort((a, b) => {
      const aHasMobile = /mobile/i.test(a.planName || '');
      const bHasMobile = /mobile/i.test(b.planName || '');
      if (aHasMobile === bHasMobile) return Number(a.price) - Number(b.price);
      return aHasMobile ? 1 : -1;
    });

  // Featured = highest-priced non-mobile plan
  const getFeaturedId = () => {
    if (!filteredPlans?.length) return null;
    const nonMobile = filteredPlans.filter((p) => !/mobile/i.test(p.planName || ''));
    if (!nonMobile.length) return null;
    return nonMobile.reduce((max, p) => (Number(p.price) > Number(max.price) ? p : max)).id;
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllSubscriptionPlanList());
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setModalState({ type: null });
      setCouponCode('');
      setCouponInput('');
      setActiveCouponPlanId(null);
      setSelectedPlan(null);
      setValidCoupon(false);
      setCouponFixedPrice(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (modalState.type !== 'success') return;
    const id = window.setTimeout(() => {
      setModalState({ type: null });
      onClose();
    }, 1800);
    return () => window.clearTimeout(id);
  }, [modalState.type, onClose]);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      const existing = document.getElementById('razorpay-script');
      if (existing) { resolve(true); return; }
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const startRazorpayPayment = async (paymentResponse: any, planId?: string) => {
    try {
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) { toast.error('Razorpay SDK failed to load'); return; }
      if (paymentResponse?.provider === 'RAZORPAY') {
        const options = {
          key: paymentResponse.keyId,
          amount: paymentResponse.amount,
          currency: paymentResponse.currency,
          name: paymentResponse.name,
          description: paymentResponse.description,
          order_id: paymentResponse.orderId,
          prefill: paymentResponse.prefill,
          handler: async () => {
            if (planId) {
              try {
                const result: any = await dispatch(subscriptiontUpgrade({ planId }));
                if (result.meta.requestStatus === 'fulfilled') {
                  setModalState({ type: 'success', message: 'Plan activated successfully 🎉' });
                  dispatch(getActivationStatus());
                } else {
                  toast.success('Payment successful! Go to billing settings to activate your plan.');
                }
              } catch {
                toast.success('Payment successful! Go to billing settings to activate your plan.');
              }
            } else {
              toast.success('Payment successful 🎉');
            }
          },
          modal: { ondismiss: () => toast.error('Payment cancelled') },
        };
        const rzp = new (window as any).Razorpay(options);
        rzp.open();
      }
    } catch {
      toast.error('Unable to start Razorpay');
    }
  };

  const getEffectivePrice = (plan: any): number =>
    validCoupon && activeCouponPlanId === plan.id ? (couponFixedPrice ?? 0) : Number(plan.price || 0);

  const handleSubscribe = async (plan: any) => {
    const price = getEffectivePrice(plan);
    if (isUpgradeFlow) {
      const remain = Number(user?.balance || 0) - price;
      setRemainingBalance(remain);
      setModalState({ type: 'confirm', plan });
      return;
    }
    try {
      const response: any = await dispatch(
        addMoneyToWallet({ amount: price, currency: 'INR', planId: plan.id })
      );
      if (response.meta.requestStatus === 'fulfilled') {
        const paymentResponse = response.payload.data.data.paymentResponse;
        toast.success('Redirecting to Razorpay...');
        startRazorpayPayment(paymentResponse, plan.id);
      }
    } catch {
      toast.error('Payment failed');
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!modalState.plan) return;
    const price = getEffectivePrice(modalState.plan);
    const remain = Number(user?.balance || 0) - price;
    setRemainingBalance(remain);

    if (remain >= 0) {
      try {
        const response: any = await dispatch(subscriptiontUpgrade({ planId: modalState.plan.id }));
        if (response.meta.requestStatus === 'fulfilled') {
          setModalState({ type: 'success', message: 'Plan upgraded successfully 🎉' });
          dispatch(getActivationStatus());
        } else {
          setModalState({ type: 'error', message: 'Upgrade failed' });
        }
      } catch {
        setModalState({ type: 'error', message: 'Something went wrong' });
      }
      return;
    }

    try {
      const response: any = await dispatch(
        addMoneyToWallet({ amount: price, currency: 'INR', planId: modalState.plan.id })
      );
      if (response.meta.requestStatus === 'fulfilled') {
        const paymentResponse = response.payload.data.data.paymentResponse;
        toast.success('Redirecting to Razorpay...');
        startRazorpayPayment(paymentResponse, modalState.plan.id);
      }
    } catch {
      toast.error('Payment failed');
    }
  };

  const FIXED_PRICE_COUPONS: Record<string, number> = { LAUNCH1180: 1180 };

  const handleCouponApply = async (plan: any) => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    const code = couponInput.trim().toUpperCase();

    if (code in FIXED_PRICE_COUPONS) {
      toast.success('Coupon applied!');
      setValidCoupon(true);
      setCouponFixedPrice(FIXED_PRICE_COUPONS[code]);
      setSelectedPlan(plan);
      setActiveCouponPlanId(plan.id);
      setCouponInput('');
      setCouponLoading(false);
      return;
    }

    const hardcoded = ['JOBNAGAR', 'NAAZ10'];
    const isHardcoded = hardcoded.includes(code);
    const isWholesaler = allWholesalerList.some(
      // @ts-expect-error
      (w) => w.refferalCode === couponInput.trim()
    );

    if (!isHardcoded && !isWholesaler) {
      toast.error('Invalid coupon code');
      setCouponLoading(false);
      return;
    }

    if (plan.durationUnit !== 'Months' || plan.durationValue !== 3) {
      toast.error('Coupon applies to 3-month plans only');
      setCouponLoading(false);
      return;
    }

    try {
      const response: any = await dispatch(verifyCouponCode({ refferalCode: couponInput.trim() }));
      if (response.meta.requestStatus === 'fulfilled') {
        toast.success('Coupon applied!');
        setValidCoupon(true);
        setCouponFixedPrice(null);
        setSelectedPlan(plan);
        setActiveCouponPlanId(plan.id);
        setCouponInput('');
      } else {
        toast.error('Coupon verification failed');
      }
    } catch {
      toast.error('Error verifying coupon');
    }
    setCouponLoading(false);
  };

  useEffect(() => {
    const fetchCurrency = async () => {
      const countryCode = await getUserCountry();
      if (countryCode && currencyMap[countryCode]) {
        setCurrencySymbol(currencyMap[countryCode].symbol);
        setUserCurrencyCode(currencyMap[countryCode].code);
      }
    };
    fetchCurrency();
  }, []);

  useEffect(() => {
    if (!userCurrencyCode) return;
    const fetchRate = async () => {
      const result = await getUserExchangeRateWithCountry('INR');
      if (result) {
        setExchangeRate(result.exchangeRate);
        if (result.userCurrencyCode !== userCurrencyCode) {
          setUserCurrencyCode(result.userCurrencyCode);
          const entry = Object.entries(currencyMap).find(
            ([, c]) => c.code === result.userCurrencyCode
          );
          if (entry) setCurrencySymbol(entry[1].symbol);
        }
      }
    };
    fetchRate();
  }, [userCurrencyCode]);

  const convertAmount = (amount: number | string): string => {
    const n = typeof amount === 'number' ? amount : Number(amount);
    if (Number.isNaN(n) || n === 0) return '0';
    if (typeof exchangeRate === 'number' && exchangeRate > 0) {
      return (n * exchangeRate).toFixed(2);
    }
    return n.toFixed(0);
  };

  const formatPlanDuration = (v?: number, u?: string) => `${v || 1} ${u || 'Month'}`;

  const ESTORE_FEATURES = [
    'Custom domain + professional themes',
    'Unlimited products (digital & physical)',
    'Inventory tracking & SKU management',
    'Multiple payment gateways',
    'SEO tools & management',
    'Referral program',
    'Order management & invoice generation',
  ];

  const MARKETPLACE_FEATURES = [
    'Everything in E-Store',
    'One-click product import & dropshipping',
    'Supplier dashboard & wholesale pricing',
    'Automatic inventory sync & real-time stock',
    'Profit margin control & supplier collaboration',
    'Trending products feed & supplier ratings',
    'Automated order forwarding',
    'Reseller growth tools & one-click store launch',
  ];

  const getPlanFeatures = (plan: any): string[] => {
    const name = (plan.planName || '').toLowerCase();
    const price = Number(plan.price || 0);
    if (/wholesale|supplier|marketplace|distribut/i.test(name)) return MARKETPLACE_FEATURES;
    if (/resell|vendor|store|retail|e-store|estore/i.test(name)) return ESTORE_FEATURES;
    // Fallback: higher-priced plan gets marketplace features
    if (filteredPlans && filteredPlans.length > 1) {
      const maxPrice = Math.max(...(filteredPlans.map((p: any) => Number(p.price || 0))));
      if (price === maxPrice) return MARKETPLACE_FEATURES;
    }
    return ESTORE_FEATURES;
  };

  const sanitizePlanDescription = (html?: string) => {
    if (!html) return 'No description available.';
    return html
      .replace(/\sstyle=(['"]).*?\1/gi, '')
      .replace(/\scolor=(['"]).*?\1/gi, '')
      .replace(/<font\b[^>]*>/gi, '')
      .replace(/<\/font>/gi, '');
  };

  const getPlanStatus = (data: any) => {
    if (!data) return '';
    const now = new Date();
    const end = new Date(data.endDate);
    if (data.isTrial === 1 && end < now) return 'Trial Expired';
    if (data.isActive === 0 && data.isTrial === 1 && end > now) return 'Trial Plan';
    if (data.isActive === 1 && end > now) return 'Active';
    if (data.isActive === 1 && end < now) return 'Expired';
    return '';
  };

  const statusText = getPlanStatus(activationStatus?.data);
  const featuredId = getFeaturedId();

  if (!isOpen) return null;

  return (
    <>
      {/* ── Main overlay ── */}
      <motion.div
        className="fixed inset-0 z-[1000] overflow-y-auto bg-[rgba(15,23,42,0.65)] px-4 py-8 backdrop-blur-sm sm:px-6 sm:py-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="mx-auto flex min-h-full max-w-5xl items-center justify-center">
          <motion.div
            className="relative w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-card"
            initial={{ opacity: 0, transform: 'translateY(32px) scale(0.97)' }}
            animate={{ opacity: 1, transform: 'translateY(0px) scale(1)' }}
            transition={{ duration: 0.38, ease }}
          >
            {/* Gradient halo */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-52 bg-white" />

            {/* Close */}
            <button
              onClick={onClose}
              className="absolute right-5 top-5 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:bg-gray-50 hover:text-gray-900"
              aria-label="Close"
            >
              <PiXBold className="h-4 w-4" />
            </button>

            <div className="relative p-6 sm:p-8 lg:p-10">
              {/* Header */}
              <div className="max-w-2xl">
                {statusText && (
                  <div className="inline-flex items-center gap-2 rounded-full border border-[#fde68a] bg-[#fffbeb] px-3 py-1 text-xs font-medium text-[#b45309]">
                    <PiLightningFill className="h-3.5 w-3.5" />
                    {statusText}
                  </div>
                )}
                <h2 className="mt-4 font-lexend text-[28px] font-semibold tracking-[-0.04em] text-gray-900 sm:text-[36px]">
                  Choose your plan
                </h2>
                <p className="mt-2 max-w-xl text-[15px] leading-7 text-gray-500">
                  Unlock premium features, billing tools, and full store operations.
                </p>
              </div>

              {/* Toggle */}
              <div className="mt-6 flex items-center gap-4">
                {hasYearlyPlans && (
                <div className="flex rounded-full border border-gray-200 bg-gray-100 p-1">
                  {(['Monthly', 'Yearly'] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => setPlanType(t)}
                      className={`rounded-full px-5 py-1.5 text-sm font-semibold transition ${
                        planType === t
                          ? 'bg-white text-gray-900 shadow'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                )}
                {planType === 'Yearly' && (
                  <motion.span
                    initial={{ opacity: 0, transform: 'translateX(-8px)' }}
                    animate={{ opacity: 1, transform: 'translateX(0px)' }}
                    className="rounded-full bg-primary-lighter px-3 py-1 text-xs font-semibold text-primary-dark border border-[#a7f3d0]"
                  >
                    Save up to 20%
                  </motion.span>
                )}
              </div>

              {/* Plan cards */}
              {filteredPlans?.length ? (
                <div className="mt-8 grid gap-5 lg:grid-cols-2">
                  {filteredPlans.map((plan, index) => {
                    const isFeatured = plan.id === featuredId;
                    const isDiscounted = validCoupon && activeCouponPlanId === plan.id;
                    const showCouponBox = activeCouponPlanId === plan.id && !validCoupon;
                    const discountedPrice = isDiscounted ? (couponFixedPrice ?? 0) : plan.price;

                    return (
                      <motion.div
                        key={plan.id || index}
                        initial={{ opacity: 0, transform: 'translateY(20px)' }}
                        animate={{ opacity: 1, transform: 'translateY(0px)' }}
                        transition={{ delay: index * 0.08, duration: 0.35, ease }}
                        className="relative flex flex-col overflow-hidden rounded-2xl border p-6 shadow-card transition-transform duration-300 hover:-translate-y-1"
                        style={{
                          borderColor: isFeatured ? '#312e81' : '#e2e8f0',
                          background: isFeatured
                            ? '#1e1b4b'
                            : 'linear-gradient(to bottom, #ffffff, #f8fafc)',
                          color: isFeatured ? '#ffffff' : 'rgb(var(--primary-default))',
                        }}
                      >
                        {/* Best Value badge */}
                        {isFeatured && (
                          <div className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium backdrop-blur-sm" style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.9)' }}>
                            <PiCrownSimpleBold className="h-3.5 w-3.5" style={{ color: '#fbbf24' }} />
                            Best Value
                          </div>
                        )}

                        {/* Plan name & duration */}
                        <div>
                          <p className="text-xs font-medium" style={{ color: isFeatured ? '#a3a3a3' : '#94a3b8' }}>
                            {formatPlanDuration(plan.durationValue, plan.durationUnit)}
                          </p>
                          <h3 className="mt-2 text-[26px] font-semibold tracking-[-0.03em]" style={{ color: isFeatured ? '#ffffff' : 'rgb(var(--primary-default))' }}>
                            {plan.planName || 'Subscription Plan'}
                          </h3>
                          <div className="mt-3 flex items-baseline gap-1">
                            <span className="text-[38px] font-bold tracking-[-0.04em]" style={{ color: isFeatured ? '#ffffff' : 'rgb(var(--primary-default))' }}>
                              {currencySymbol}{convertAmount(discountedPrice)}
                            </span>
                            {!isDiscounted && (
                              <span className="text-sm" style={{ color: isFeatured ? '#a3a3a3' : '#64748b' }}>
                                / {formatPlanDuration(plan.durationValue, plan.durationUnit)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Description */}
                        <div
                          className="mt-4 rounded-[16px] border px-4 py-3.5 text-sm leading-6"
                          style={{
                            borderColor: isFeatured ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
                            background: isFeatured ? 'rgba(255,255,255,0.05)' : '#ffffff',
                            color: isFeatured ? '#c7d2fe' : '#475569',
                          }}
                        >
                          {/wholesale|supplier|marketplace|distribut/i.test(plan.planName || '')
                            || (filteredPlans && filteredPlans.length > 1 && Number(plan.price) === Math.max(...filteredPlans.map((p: any) => Number(p.price || 0))))
                            ? 'For Manufacturers, Wholesalers & Distributors — build your ecosystem and power thousands of resellers.'
                            : 'For Store Owners, Resellers & Dropshippers — build your online store with everything you need to sell.'}
                        </div>

                        {/* Features */}
                        <div
                          className="mt-4 rounded-[16px] border px-4 py-3.5"
                          style={{
                            borderColor: isFeatured ? 'rgba(255,255,255,0.1)' : '#f1f5f9',
                            background: isFeatured ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                          }}
                        >
                          <p className="text-xs font-medium" style={{ color: isFeatured ? '#a3a3a3' : '#94a3b8' }}>
                            Included
                          </p>
                          <div className="mt-2.5 space-y-2">
                            {getPlanFeatures(plan).map((f) => (
                              <div key={f} className="flex items-center gap-2.5 text-sm" style={{ color: isFeatured ? '#c7d2fe' : '#475569' }}>
                                <PiCheckCircleFill className="h-4 w-4 flex-shrink-0" style={{ color: isFeatured ? '#34d399' : '#10b981' }} />
                                {f}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Coupon toggle */}
                        {!validCoupon || activeCouponPlanId !== plan.id ? (
                          <button
                            type="button"
                            onClick={() => setActiveCouponPlanId(activeCouponPlanId === plan.id ? null : plan.id)}
                            className="mt-3 flex items-center gap-1.5 text-xs font-medium transition"
                            style={{ color: isFeatured ? '#a3a3a3' : '#94a3b8' }}
                          >
                            <PiTagBold className="h-3.5 w-3.5" />
                            {activeCouponPlanId === plan.id ? 'Hide coupon' : 'Have a coupon?'}
                          </button>
                        ) : (
                          <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#10b981' }}>
                            <PiSealCheckFill className="h-4 w-4" />
                            {couponFixedPrice != null ? `Coupon applied — ${currencySymbol}${convertAmount(couponFixedPrice)}!` : 'Coupon applied — free!'}
                          </div>
                        )}

                        {/* Coupon input */}
                        <AnimatePresence>
                          {activeCouponPlanId === plan.id && !validCoupon && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="mt-2 overflow-hidden"
                            >
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={couponInput}
                                  onChange={(e) => setCouponInput(e.target.value)}
                                  placeholder="Enter code"
                                  className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleCouponApply(plan)}
                                  disabled={!couponInput.trim() || couponLoading}
                                  className="rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-white transition hover:bg-primary disabled:opacity-50"
                                >
                                  Apply
                                </button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* CTA */}
                        <button
                          onClick={() => handleSubscribe(plan)}
                          className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-2xl text-sm font-semibold transition"
                          style={
                            isFeatured
                              ? { background: '#ffffff', color: '#1e1b4b' }
                              : { background: '#1e1b4b', color: '#ffffff' }
                          }
                        >
                          Continue with {plan.planName || 'this plan'}
                          <PiArrowRightBold className="h-4 w-4" />
                        </button>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-12 flex flex-col items-center justify-center gap-2 py-8 text-gray-400">
                  <PiLightningFill className="h-8 w-8 opacity-30" />
                  <p className="text-sm">No {planType} plans available.</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* ── State modals (confirm / error / success) ── */}
      <AnimatePresence>
        {modalState.type && (
          <motion.div
            className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-2xl"
              initial={{ opacity: 0, transform: 'translateY(16px) scale(0.92)' }}
              animate={{ opacity: 1, transform: 'translateY(0px) scale(1)' }}
              exit={{ opacity: 0, transform: 'translateY(8px) scale(0.94)' }}
              transition={{ duration: 0.26, ease }}
            >
              {/* ERROR */}
              {modalState.type === 'error' && (
                <>
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-soft text-danger">
                    <PiWarningBold className="h-6 w-6" />
                  </div>
                  <h2 className="mt-4 text-xl font-semibold text-gray-900">Insufficient balance</h2>
                  <p className="mt-2 text-sm text-gray-500">
                    Top up your wallet to continue with this plan.
                  </p>
                  {modalState.plan && (
                    <div className="mt-4 rounded-2xl bg-gray-50 p-4 text-sm space-y-1.5">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Current balance</span>
                        <span className="font-semibold text-gray-900">₹{user?.balance}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Required</span>
                        <span className="font-semibold text-gray-900">₹{getEffectivePrice(modalState.plan)}</span>
                      </div>
                    </div>
                  )}
                  <div className="mt-5 flex gap-3">
                    <button
                      onClick={() => setModalState({ type: null })}
                      className="flex-1 rounded-2xl border border-gray-200 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => { setModalState({ type: null }); router.push(routes.wallets); }}
                      className="flex-1 rounded-2xl bg-[#1e1b4b] py-3 text-sm font-semibold text-white transition hover:bg-[#312e81]"
                    >
                      Recharge wallet
                    </button>
                  </div>
                </>
              )}

              {/* CONFIRM */}
              {modalState.type === 'confirm' && (
                <>
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-primary">
                    <PiWalletBold className="h-6 w-6" />
                  </div>
                  <h2 className="mt-4 text-xl font-semibold text-gray-900">Confirm upgrade</h2>
                  <p className="mt-2 text-sm text-gray-500">
                    Review your wallet balance before upgrading.
                  </p>
                  <div className="mt-4 rounded-2xl bg-gray-50 p-4 text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Wallet balance</span>
                      <span className="font-semibold text-gray-900">₹{user?.balance}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Plan price</span>
                      <span className="font-semibold text-gray-900">₹{getEffectivePrice(modalState.plan)}</span>
                    </div>
                    <div className="my-1 h-px bg-gray-200" />
                    <div className="flex justify-between">
                      <span className="text-gray-500">After upgrade</span>
                      <span className={`font-semibold ${(remainingBalance ?? 0) < 0 ? 'text-[#ef4444]' : 'text-primary'}`}>
                        ₹{remainingBalance?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {(remainingBalance ?? 0) <= 0 && (
                    <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-[#ef4444]">
                      <PiWarningBold className="h-3.5 w-3.5" />
                      Insufficient balance — please top up first
                    </p>
                  )}
                  <div className="mt-5 flex gap-3">
                    <button
                      onClick={() => setModalState({ type: null })}
                      className="flex-1 rounded-2xl border border-gray-200 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    {(remainingBalance ?? 0) <= 0 ? (
                      <button
                        onClick={() => { setModalState({ type: null }); router.push(routes.wallets); }}
                        className="flex-1 rounded-2xl bg-[#1e1b4b] py-3 text-sm font-semibold text-white transition hover:bg-[#312e81]"
                      >
                        Recharge
                      </button>
                    ) : (
                      <button
                        onClick={handleConfirmUpgrade}
                        className="flex-1 rounded-2xl bg-[#1e1b4b] py-3 text-sm font-semibold text-white transition hover:bg-[#312e81]"
                      >
                        Confirm
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* SUCCESS */}
              {modalState.type === 'success' && (
                <div className="flex flex-col items-center text-center">
                  <motion.div
                    className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-lighter text-primary"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 16 }}
                  >
                    <PiSealCheckFill className="h-9 w-9" />
                  </motion.div>
                  <h2 className="mt-5 text-xl font-semibold text-gray-900">You're all set!</h2>
                  <p className="mt-2 text-sm text-gray-500">{modalState.message || 'Plan activated successfully.'}</p>
                  <button
                    onClick={() => { setModalState({ type: null }); onClose(); }}
                    className="mt-6 w-full rounded-2xl bg-[#1e1b4b] py-3 text-sm font-semibold text-white transition hover:bg-[#312e81]"
                  >
                    Continue to dashboard
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SubscriptionPurchaseModal;
